// @ts-nocheck
import React, { useState, useEffect } from "react"
import { 
  Box, 
  Container, 
  Text, 
  Grid, 
  GridItem, 
  VStack,
  HStack,
  Flex,
  Button,
  Heading,
  Badge
} from "@chakra-ui/react"
import { createFileRoute } from "@tanstack/react-router"
import { MdMessage, MdMail, MdLocalPhone} from "react-icons/md"

import useAuth from "@/hooks/useAuth"
import EquipmentDetailDialog from "@/components/Dashboard/EquipmentDetailDialog"
import PredictiveMaintenanceDashboard from "@/components/Dashboard/PredictiveMaintenanceDashboard"
import ChatBot from "@/components/Dashboard/Chatbot"

export const Route = createFileRoute("/_layout/")({
  component: Dashboard,
})

function Dashboard() {
  const { user: currentUser } = useAuth()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedEquipment, setSelectedEquipment] = useState(null)
  const [activeView, setActiveView] = useState('monitoring') // 'monitoring' 또는 'predictive'
  
  // 샘플 데이터 상태
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalEquipment: 18,
      onlineEquipment: 16,
      alerts: 2,
      productivity: 92.3
    },
    equipmentStatus: [
      { id: 1, name: 'CNC 밀링머신 #1', status: '가동중', temperature: 48.23, vibration: 2.15, efficiency: 94.67, spindle: 8500.12, feed: 1200.45 },
      { id: 2, name: 'CNC 선반 #3', status: '가동중', temperature: 44.89, vibration: 1.82, efficiency: 91.23, spindle: 3200.67, feed: 850.90 },
      { id: 3, name: '5축 머시닝센터 #2', status: '경고', temperature: 73.45, vibration: 4.23, efficiency: 68.12, spindle: 12000.34, feed: 2500.78 },
      { id: 4, name: 'CNC 보링머신 #1', status: '이상감지', temperature: 23.12, vibration: 0, efficiency: 0, spindle: 0, feed: 0 },
      { id: 5, name: 'CNC 선반 #1', status: '가동중', temperature: 42.67, vibration: 1.64, efficiency: 88.45, spindle: 2800.23, feed: 950.12 },
      { id: 6, name: 'CNC 밀링머신 #4', status: '가동중', temperature: 46.34, vibration: 1.91, efficiency: 96.78, spindle: 9200.56, feed: 1450.89 },
      { id: 7, name: 'CNC 드릴링머신 #2', status: '가동중', temperature: 38.91, vibration: 2.84, efficiency: 82.56, spindle: 4500.78, feed: 380.45 },
      { id: 8, name: 'CNC 그라인더 #1', status: '경고', temperature: 78.23, vibration: 4.87, efficiency: 65.34, spindle: 18000.12, feed: 120.67 },
      { id: 9, name: '수직 머시닝센터 #5', status: '가동중', temperature: 51.56, vibration: 2.34, efficiency: 89.12, spindle: 10500.89, feed: 1800.23 }
    ],
    alerts: [
      { id: 1, type: 'warning', message: '5축 머시닝센터 #2 스핀들 온도 상승', time: '3분 전', severity: 'high' },
      { id: 2, type: 'error', message: 'CNC 보링머신 #1 비상정지 발생', time: '12분 전', severity: 'critical' },
      { id: 3, type: 'warning', message: 'CNC 그라인더 #1 진동 수치 임계값 초과', time: '8분 전', severity: 'high' },
    ],
    productionData: [
      { time: '06:00', production: 145, target: 160 },
      { time: '08:00', production: 168, target: 160 },
      { time: '10:00', production: 175, target: 160 },
      { time: '12:00', production: 152, target: 160 },
      { time: '14:00', production: 188, target: 160 },
      { time: '16:00', production: 171, target: 160 }
    ]
  })

  // AI 알림 데이터
  const [aiAlerts, setAiAlerts] = useState([
    {
      id: 1,
      equipmentId: 3,
      type: 'prediction',
      severity: 'high',
      message: '5축 머시닝센터 #2, 72시간 내 냉각시스템 고장 예측 (확률: 78%)',
      aiConfidence: 78,
      recommendedAction: '냉각수 교체 및 펌프 점검',
      estimatedCost: '150만원',
      time: '5분 전'
    },
    {
      id: 2,
      equipmentId: 8,
      type: 'anomaly',
      severity: 'medium',
      message: 'CNC 그라인더 #1, 비정상 진동 패턴 감지',
      aiConfidence: 65,
      recommendedAction: '베어링 상태 점검',
      estimatedCost: '80만원',
      time: '12분 전'
    },
    {
      id: 3,
      equipmentId: 1,
      type: 'optimization',
      severity: 'low',
      message: 'CNC 밀링머신 #1, 스핀들 속도 최적화로 15% 효율 향상 가능',
      aiConfidence: 82,
      recommendedAction: '가공 파라미터 조정',
      estimatedCost: '무료',
      time: '25분 전'
    }
  ])

  // 실시간 업데이트 시뮬레이션
  useEffect(() => {
    const interval = setInterval(() => {
      setDashboardData(prev => ({
        ...prev,
        equipmentStatus: prev.equipmentStatus.map(equipment => {
          if (equipment.status !== '가동중') return equipment
          
          return {
            ...equipment,
            temperature: Math.round(Math.max(30, Math.min(80, 
              equipment.temperature + (Math.random() - 0.5) * 1)) * 100) / 100,
            vibration: Math.round(Math.max(0, Math.min(5, 
              equipment.vibration + (Math.random() - 0.5) * 0.1)) * 100) / 100,
            efficiency: Math.round(Math.max(0, Math.min(100, 
              equipment.efficiency + (Math.random() - 0.5) * 2)) * 100) / 100,
            spindle: Math.round(Math.max(0, Math.min(25000, 
              equipment.spindle + (Math.random() - 0.5) * 200)) * 100) / 100,
            feed: Math.round(Math.max(0, Math.min(3000, 
              equipment.feed + (Math.random() - 0.5) * 50)) * 100) / 100
          }
        })
      }))
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  const getStatusColor = (status) => {
    switch(status) {
      case '가동중': return '#28a745'
      case '경고': return '#ffc107'
      case '이상감지': return '#dc3545'
      default: return '#6c757d'
    }
  }

  const getAlertIcon = (severity) => {
    switch(severity) {
      case 'critical': return '🚨'
      case 'high': return '⚠️'
      case 'low': return 'ℹ️'
      default: return 'ℹ️'
    }
  }

  const handleEquipmentClick = (equipment) => {
    setSelectedEquipment(equipment)
    setIsDialogOpen(true)
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setSelectedEquipment(null)
  }

  const CustomCard = ({ children, ...props }) => (
    <Box 
      bg="white" 
      border="1px" 
      borderColor="gray.200" 
      borderRadius="md" 
      p={4} 
      shadow="md"
      {...props}
    >
      {children}
    </Box>
  )

  const StatCard = ({ icon, label, value, helpText, valueColor = "#3182ce" }) => (
    <Box textAlign="center">
      <Text fontSize="sm" color="gray.600" mb={1}>
        {icon} {label}
      </Text>
      <Text fontSize="3xl" fontWeight="bold" color={valueColor} mb={1}>
        {value}
      </Text>
      <Text fontSize="xs" color="gray.500">
        {helpText}
      </Text>
    </Box>
  )

  const SimpleProgress = ({ value, max = 100, color = "#3182ce", height = "8px" }) => (
    <Box w="100%" bg="gray.200" borderRadius="md" overflow="hidden" h={height}>
      <Box 
        h="100%" 
        bg={color} 
        borderRadius="md"
        w={`${Math.min((value / max) * 100, 100)}%`}
        transition="width 0.3s ease"
      />
    </Box>
  )

//   <Box 
//   key={equipment.id} 
//   border="1px solid" 
//   borderColor="gray.300" 
//   borderRadius="md" 
//   p={3} 
//   bg="gray.50"
//   shadow="md"
//   cursor="pointer"
//   transition="all 0.2s"
//   _hover={{
//     transform: "translateY(-2px)",
//     shadow: "lg",
//     borderColor: "blue.500",
//   }}
//   onClick={() => handleEquipmentClick(equipment)}
// >

  // AI 알림 카드
  const AIAlertsCard = () => (
    <CustomCard bg="#F5F5F5">
      <Heading size="lg" mb={4}>AI 예지보전 알림</Heading>
      <VStack gap={4} align="stretch">
        {aiAlerts.map(alert => (
          <Box 
            key={alert.id}
            p={3}
            bg="gray.50"
            borderRadius="md"
            border="1px solid" 
            borderColor="gray.300"
            shadow="md"
            cursor="pointer"
            transition="all 0.2s"
            _hover={{
              transform: "translateY(-2px)",
              shadow: "lg",
              borderColor: "blue.500",
            }}
          >
            <VStack align="stretch" gap={2}>
              <HStack justify="space-between">
                <Text fontSize="md" fontWeight="semibold" color="gray.800">
                  {alert.message}
                </Text>
                <Badge 
                  bg={alert.aiConfidence < 70 ? "yellow.500" : "red.500"} 
                  size="md" 
                  color="white"
                >
                  AI {alert.aiConfidence}%
                </Badge>
              </HStack>
              
              <Text fontSize="md" color="gray.600">
                📋 권장: {alert.recommendedAction}
              </Text>
              
              <HStack justify="space-between">
                <Text fontSize="md" color="gray.500">{alert.time}</Text>
                <Text fontSize="md" color="gray.500">
                  예상비용: {alert.estimatedCost}
                </Text>
              </HStack>
            </VStack>
          </Box>
        ))}
      </VStack>
    </CustomCard>
  )

  // 실시간 모니터링 뷰
  const MonitoringView = () => (
    <>
      {/* 개요 카드 */}
      {/* <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={6} mb={8}> */}
        {/* 개요 카드 */}
        <HStack w="100%" spacing={5} mb={8}>
          {/* 전체 장비 */}
          <Box 
            flex={1}
            h="120px"
            px={6}
            position="relative"
            bg="#F5F5F5"
            borderRadius="10px"
            display="flex"
            alignItems="center"
            gap={5}
          >
            <Box 
              w="86px" 
              h="86px" 
              bg="white" 
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="img"
                src="/assets/dashboard/fi3.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
              />
            </Box>
            
            <VStack align="flex-start" spacing={0}>
              <Text 
                color="#1A1A1A" 
                fontSize="16px" 
                fontWeight="700" 
                lineHeight="25.60px"
                fontFamily="Noto Sans KR"
              >
                전체 장비
              </Text>
              
              <HStack align="flex-end" spacing={1}>
                <Text 
                  color="#1A1A1A" 
                  fontSize="42px" 
                  fontWeight="700" 
                  lineHeight="67.20px"
                  fontFamily="Noto Sans KR"
                >
                  {dashboardData.overview.totalEquipment}
                </Text>
                <Box pb="5px">
                  <Text 
                    color="#1A1A1A" 
                    fontSize="28px" 
                    fontWeight="700" 
                    lineHeight="44.80px"
                    fontFamily="Noto Sans KR"
                  >
                    ea
                  </Text>
                </Box>
              </HStack>
            </VStack>
            
            <Box 
              position="absolute" 
              right="25px" 
              top="10.5px"
              px="7px" 
              py="3px"
              bg="#777777" 
              borderRadius="5.25px"
            >
              <Text 
                color="white" 
                fontSize="12px" 
                fontWeight="700" 
                lineHeight="19.20px"
                fontFamily="Noto Sans KR"
              >
                CNC 장비 수
              </Text>
            </Box>
          </Box>

          {/* 가동 중 */}
          <Box 
            flex={1}
            h="120px"
            px={6}
            position="relative"
            bg="#F5F5F5"
            borderRadius="10px"
            display="flex"
            alignItems="center"
            gap={5}
          >
            <Box 
              w="86px" 
              h="86px" 
              bg="white" 
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="img"
                src="/assets/dashboard/fi2.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
              />
            </Box>
            
            <VStack align="flex-start" spacing={0}>
              <Text 
                color="#1A1A1A" 
                fontSize="16px" 
                fontWeight="700" 
                lineHeight="25.60px"
                fontFamily="Noto Sans KR"
              >
                가동 중
              </Text>
              
              <HStack align="flex-end" spacing={1}>
                <Text 
                  color="#1A1A1A" 
                  fontSize="42px" 
                  fontWeight="700" 
                  lineHeight="67.20px"
                  fontFamily="Noto Sans KR"
                >
                  {dashboardData.overview.onlineEquipment}
                </Text>
                <Box pb="5px">
                  <Text 
                    color="#1A1A1A" 
                    fontSize="28px" 
                    fontWeight="700" 
                    lineHeight="44.80px"
                    fontFamily="Noto Sans KR"
                  >
                    ea
                  </Text>
                </Box>
              </HStack>
            </VStack>
            
            <Box 
              position="absolute" 
              right="25px" 
              top="10.5px"
              px="7px" 
              py="3px"
              bg="#28A745" 
              borderRadius="5.25px"
            >
              <Text 
                color="white" 
                fontSize="12px" 
                fontWeight="700" 
                lineHeight="19.20px"
                fontFamily="Noto Sans KR"
              >
                정상 작동 중
              </Text>
            </Box>
          </Box>

          {/* 알림 */}
          <Box 
            flex={1}
            h="120px"
            px={6}
            position="relative"
            bg="#F5F5F5"
            borderRadius="10px"
            display="flex"
            alignItems="center"
            gap={5}
          >
            <Box 
              w="86px" 
              h="86px" 
              bg="white" 
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="img"
                src="/assets/dashboard/fi4.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
              />
            </Box>
            
            <VStack align="flex-start" spacing={0}>
              <Text 
                color="#1A1A1A" 
                fontSize="16px" 
                fontWeight="700" 
                lineHeight="25.60px"
                fontFamily="Noto Sans KR"
              >
                알림
              </Text>
              
              <HStack align="flex-end" spacing={1}>
                <Text 
                  color="#DC3545" 
                  fontSize="42px" 
                  fontWeight="700" 
                  lineHeight="67.20px"
                  fontFamily="Noto Sans KR"
                >
                  {dashboardData.overview.alerts}
                </Text>
                <Box pb="6px">
                  <Text 
                    color="#1A1A1A" 
                    fontSize="28px" 
                    fontWeight="700" 
                    lineHeight="44.80px"
                    fontFamily="Noto Sans KR"
                  >
                    건
                  </Text>
                </Box>
              </HStack>
            </VStack>
            
            <Box 
              position="absolute" 
              right="25px" 
              top="10.5px"
              px="7px" 
              py="3px"
              bg="#DC3545" 
              borderRadius="5.25px"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Text 
                color="white" 
                fontSize="12px" 
                fontWeight="700" 
                lineHeight="19.20px"
                fontFamily="Noto Sans KR"
              >
                🚨확인요망!
              </Text>
            </Box>
          </Box>

          {/* 가동률 */}
          <Box 
            flex={1}
            h="120px"
            px={6}
            position="relative"
            bg="#F5F5F5"
            borderRadius="10px"
            display="flex"
            alignItems="center"
            gap={5}
          >
            <Box 
              w="86px" 
              h="86px" 
              bg="white" 
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="img"
                src="/assets/dashboard/fi1.svg"
                alt="Equipment Icon"
                w="50px"
                h="50px"
              />
            </Box>
            
            <VStack align="flex-start" spacing={0}>
              <Text 
                color="#1A1A1A" 
                fontSize="16px" 
                fontWeight="700" 
                lineHeight="25.60px"
                fontFamily="Noto Sans KR"
              >
                가동률
              </Text>
              
              <HStack align="flex-end" spacing={1}>
                <Text 
                  color="#2A72F7" 
                  fontSize="42px" 
                  fontWeight="700" 
                  lineHeight="67.20px"
                  fontFamily="Noto Sans KR"
                >
                  {dashboardData.overview.productivity.toFixed(1)}
                </Text>
                <Box pb="5px">
                  <Text 
                    color="#1A1A1A" 
                    fontSize="28px" 
                    fontWeight="700" 
                    lineHeight="44.80px"
                    fontFamily="Noto Sans KR"
                  >
                    %
                  </Text>
                </Box>
              </HStack>
            </VStack>
            
            <Box 
              position="absolute" 
              right="25px" 
              top="10.5px"
              px="7px" 
              py="3px"
              bg="#2A72F7" 
              borderRadius="5.25px"
            >
              <Text 
                color="white" 
                fontSize="12px" 
                fontWeight="700" 
                lineHeight="19.20px"
                fontFamily="Noto Sans KR"
              >
                평균 효율
              </Text>
            </Box>
          </Box>
        </HStack>
      {/* </Grid> */}

      {/* 메인 콘텐츠 */}
      <Grid templateColumns={{ base: "1fr", xl: "2fr 1fr" }} gap={8} pb={8}>
        {/* 장비 상태 */}
        <GridItem>
          <CustomCard bgColor="#F5F5F5">
            <HStack justify="space-between" align="center" mb={4}>
              <Heading size="lg">CNC 장비 상태</Heading>
              <HStack gap={4}>
                <HStack gap={1}>
                  <Box w={4} h={4} bg="green.500" borderRadius="full" />
                  <Text fontSize="md" color="gray.600">가동중</Text>
                </HStack>
                <HStack gap={1}>
                  <Box w={4} h={4} bg="yellow.500" borderRadius="full" />
                  <Text fontSize="md" color="gray.600">경고</Text>
                </HStack>
                <HStack gap={1}>
                  <Box w={4} h={4} bg="red.500" borderRadius="full" />
                  <Text fontSize="md" color="gray.600">이상감지</Text>
                </HStack>
                <HStack gap={1}>
                  <Box w={4} h={4} bg="gray.500" borderRadius="full" />
                  <Text fontSize="md" color="gray.600">비가동</Text>
                </HStack>
                <HStack gap={1}>
                  <Box w={4} h={4} bg="blue.500" borderRadius="full" />
                  <Text fontSize="md" color="gray.600">점검중</Text>
                </HStack>
              </HStack>
            </HStack>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
              {dashboardData.equipmentStatus.map(equipment => (
                <Box 
                  key={equipment.id} 
                  border="1px solid" 
                  borderColor="gray.300" 
                  borderRadius="md" 
                  p={3} 
                  bg="gray.50"
                  shadow="md"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    transform: "translateY(-2px)",
                    shadow: "lg",
                    borderColor: "blue.500",
                  }}
                  onClick={() => handleEquipmentClick(equipment)}
                >
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="semibold">{equipment.name}</Text>
                    <Box 
                      px={2} 
                      py={1} 
                      borderRadius="md" 
                      bg={getStatusColor(equipment.status)}
                      color="white"
                      fontSize="md"
                      fontWeight="bold"
                    >
                      {equipment.status}
                    </Box>
                  </HStack>
                  
                  <VStack gap={3} align="stretch">
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" color="gray.600">온도</Text>
                      <HStack>
                        {equipment.temperature > 75 && <Text fontSize="sm">🚨</Text>}
                        <Text fontSize="sm" fontWeight="medium">{equipment.temperature.toFixed(2)}°C</Text>
                      </HStack>
                    </HStack>
                    <SimpleProgress 
                      value={equipment.temperature} 
                      max={80} 
                      color={equipment.temperature > 75 ? "#e53e3e" : "#626568"} // 70% of 80 = 56
                    />
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="md" color="gray.600">진동</Text>
                      <Text fontSize="md" fontWeight="medium">{equipment.vibration.toFixed(2)}mm/s</Text>
                    </HStack>
                    <SimpleProgress 
                      value={equipment.vibration * 15} 
                      max={100} 
                      color="#626568"
                    />
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="md" color="gray.600">효율</Text>
                      <Text fontSize="md" fontWeight="medium">{equipment.efficiency.toFixed(2)}%</Text>
                    </HStack>
                    <SimpleProgress 
                      value={equipment.efficiency} 
                      max={100} 
                      color="#626568"
                    />
                  </Box>
                    
                    {equipment.status === 'online' && (
                      <HStack justify="space-between" fontSize="md" color="gray.500">
                        <Text>🔄 {equipment.spindle.toFixed(2)} RPM</Text>
                        <Text>➡️ {equipment.feed.toFixed(2)} mm/min</Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>
              ))}
            </Grid>
          </CustomCard>
        </GridItem>

        {/* 사이드바 - 알림 및 생산 데이터 */}
        <GridItem>
          <VStack gap={6} align="stretch">
            {/* AI 알림 */}
            <AIAlertsCard />

            {/* 기존 알림 */}
            <CustomCard bg="#F5F5F5">
              <Heading size="lg" fontWeight="semibold" mb={4}>최근 알림</Heading>
              <VStack gap={4} align="stretch">
                {dashboardData.alerts.map(alert => (
                  <Box 
                    key={alert.id} 
                    p={3} 
                    bg="white" 
                    borderRadius="md"
                    borderLeft="4px"
                    borderLeftColor={
                      alert.severity === 'critical' ? 'red.500' :
                      alert.severity === 'high' ? 'orange.500' : 'blue.500'
                    }
                  >
                    <HStack justify="space-between">
                      <HStack>
                        <Text fontSize="lg">{getAlertIcon(alert.severity)}</Text>
                        <Box>
                          <Text fontSize="md" fontWeight="medium">{alert.message}</Text>
                          <Text fontSize="md" color="gray.500">{alert.time}</Text>
                        </Box>
                      </HStack>
                      <HStack gap={2}>
                        <MdMessage size={20} color="#6B7280" />
                        <MdMail size={20} color="#6B7280" />
                        <MdLocalPhone size={20} color="#6B7280" />
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </CustomCard>

            {/* 생산 현황 */}
            <CustomCard bg="#F5F5F5">
              <Heading size="lg" fontWeight="semibold" mb={4}>생산 현황</Heading>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between" fontSize="md">
                  <HStack>
                    <Box w={3} h={3} bg="blue.500" borderRadius="md" />
                    <Text>실제 생산</Text>
                  </HStack>
                  <HStack>
                    <Box w={3} h={3} bg="green.500" borderRadius="md" />
                    <Text>목표</Text>
                  </HStack>
                </HStack>
                
                {dashboardData.productionData.map((data, index) => (
                  <Box key={index}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="md" fontWeight="medium">{data.time}</Text>
                      <HStack gap={4}>
                        <Text fontSize="md" color="blue.500">{data.production}</Text>
                        <Text fontSize="md" color="green.500">{data.target}</Text>
                      </HStack>
                    </HStack>
                    <HStack gap={2}>
                      <Box flex={1}>
                        <SimpleProgress 
                          value={(data.production / data.target) * 100} 
                          max={150} 
                          color={data.production >= data.target ? "#38a169" : "#3182ce"}
                        />
                      </Box>
                      <Text fontSize="md" color="gray.500" minW="35px">
                        {((data.production / data.target) * 100).toFixed(0)}%
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </CustomCard>
          </VStack>
        </GridItem>
      </Grid>
    </>
  )

  return (
    <Box minH="100%" w="100%" overflow="hidden">
      <Box 
        h="100%" 
        w="100%"
        overflowY="auto" 
        pt={4} 
        px={6} 
        pb={4}
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {/* 헤더 */}
        <Box 
          // position="sticky" 
          top={0} 
          bg="white" 
          zIndex={10} 
          py={4} 
          mb={4}
          borderBottom="1px"
          borderColor="gray.200"
          mx={-6}
          px={6}
        >
          <Flex justify="space-between" align="center" mb={0}>
            <Heading size="4xl">
              IoT 실시간 모니터링
            </Heading>
            <VStack align="end" gap={1}>
              <Text fontSize="sm" color="gray.500">
                마지막 업데이트: {new Date().toLocaleTimeString()}
              </Text>
              <Button size="sm" colorScheme="blue" onClick={() => window.location.reload()}>
                🔄 새로고침
              </Button>
            </VStack>
          </Flex>
        </Box>

        <MonitoringView />
       
      </Box>

      {/* 장비 상세 다이얼로그 */}
      <EquipmentDetailDialog 
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        equipment={selectedEquipment}
      />

      {dashboardData ? <ChatBot dashboardData={dashboardData} /> : <Spinner />}
    </Box>
  )
}

export default Dashboard